import { useAuthStore } from "../store/authStore";

const BASE = "/api";

export async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });
  if (res.status === 401) { useAuthStore.getState().logout(); window.location.href = "/login"; throw new Error("Sesión expirada"); }
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail ?? `Error ${res.status}`); }
  if (res.status === 204) return undefined as T;
  return res.json();
}

type Page<T> = { total: number; page: number; limit: number; items: T[] };

/**
 * Recorre un endpoint paginado hasta traerse todas las filas.
 *
 * Los listados de portfolio/competidores se filtran en cliente, así que la
 * tabla necesita el conjunto completo: pedir sólo la primera página perdería
 * filas en silencio. El tope del backend es limit=200.
 */
export async function getAllPages<T>(path: string, pageSize = 200): Promise<Page<T>> {
  const sep = path.includes("?") ? "&" : "?";
  const fetchPage = (page: number) => apiFetch<Page<T>>(`${path}${sep}page=${page}&limit=${pageSize}`);

  const first = await fetchPage(1);
  const items = [...first.items];
  const pages = Math.ceil(first.total / pageSize);
  for (let page = 2; page <= pages; page++) {
    const next = await fetchPage(page);
    if (next.items.length === 0) break; // filas borradas entre peticiones
    items.push(...next.items);
  }
  return { ...first, items };
}

/**
 * Descarga un fichero de un endpoint protegido.
 *
 * Los botones de «Exportar Excel» hacían `a.href = "/api/export/..."` + `a.click()`.
 * Una navegación NO lleva la cabecera `Authorization`, y la API va con JWT: el
 * backend respondía **401** y el navegador no descargaba nada. Estaba roto para
 * todos, tenants y superadmin (§77).
 *
 * Hay que traerse el fichero con fetch (que sí manda el token), convertirlo en un
 * blob y disparar la descarga desde un object URL.
 */
export async function download(path: string, fallbackName: string): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) { useAuthStore.getState().logout(); window.location.href = "/login"; throw new Error("Sesión expirada"); }
  if (!res.ok) throw new Error(`Error ${res.status}`);

  // El backend manda el nombre en Content-Disposition; si no, el de reserva.
  const cd = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match?.[1] ?? fallbackName;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);   // sin esto, el blob se queda en memoria
}

export const api = {
  get: <T>(p: string) => apiFetch<T>(p),
  getAll: getAllPages,
  download,
  post: <T>(p: string, b: unknown) => apiFetch<T>(p, { method: "POST", body: JSON.stringify(b) }),
  put: <T>(p: string, b: unknown) => apiFetch<T>(p, { method: "PUT", body: JSON.stringify(b) }),
  delete: (p: string) => apiFetch(p, { method: "DELETE" }),
};
